/**
 * Where to send the browser after a sign-in — only ever an `https://` page on this tenant's apex.
 *
 * `?next=` is attacker-controlled: anybody can send a link to `login.<apex>/?next=…`. Assigned to
 * `window.location.href` as-is, `next=javascript:…` ran in THIS origin right after the session
 * cookie was written, and that cookie was readable by script — so one click and one ordinary
 * sign-in handed the caller's session (the owner's, with every role) to whoever wrote the link.
 * (Since 2026-10-02 the `jwt` cookie is HttpOnly, so script can no longer read it; this check stays
 * because script in this origin could still act AS the person.)
 * `next=https://elsewhere` was a plain open redirect, the same link minus the theft.
 *
 * So the value is parsed, not trusted: an absolute `https:` URL whose host is the apex or one of
 * its subdomains, with no credentials in it. Anything else — another scheme, another host, a
 * look-alike such as `martincastroalvarez.com.evil.io`, garbage — falls back to `fallback`.
 */
export function safeNext(raw: string | null | undefined, apex: string, fallback: string): string {
  if (!raw) return fallback;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return fallback;
  }
  const host = url.hostname.toLowerCase();
  const domain = apex.toLowerCase();
  const onApex = host === domain || host.endsWith(`.${domain}`);
  if (url.protocol !== 'https:' || !onApex || url.username || url.password) return fallback;
  return url.toString();
}
